/**
 * Canonical creative artifact export — one production primitive for:
 * ArtifactPreview / artifact-capture queue, adapter exportReel callers, and
 * the ?testSpeed=1 Playwright bridge.
 *
 * HybridVideoRendererAdapter.exportReel builds its OWN offscreen video from the
 * request's pack (self-contained; no live editor session required — works on
 * Publish/Compare/Portfolio after EditorScreen unmounts).
 */
import {
  resolveEdit,
  validateEditGraphAgainstPack,
  type EditGraph,
  type ExportResult,
  type ResolvedEdit,
  type SourcePack,
} from "@desk/core";
import {
  HybridVideoRendererAdapter,
} from "../../rendering/hybridvideo/HybridVideoRendererAdapter.js";
import {
  sampleCreativeRenderState,
} from "./creative-render-state.js";
import { shouldShowReconstructionLabel } from "../../game/disclosure.js";
import {
  buildEditedTimeline,
  editTickToSourceTick,
} from "./timeline.js";

/** Reject fractional / non-finite capture ticks — exact integer only. */
function requireIntegerTick(
  tick: number,
  label: string,
  maxInclusive: number,
): number {
  if (!Number.isFinite(tick) || !Number.isInteger(tick)) {
    throw new Error(`${label} must be a finite integer tick (got ${String(tick)})`);
  }
  if (maxInclusive < 0) return 0;
  if (tick < 0 || tick > maxInclusive) {
    throw new Error(
      `${label} out of bounds: ${tick} not in [0, ${maxInclusive}]`,
    );
  }
  return tick;
}

export interface CreativeArtifactExportRequest {
  pack: SourcePack;
  editGraph: EditGraph;
  resolved?: ResolvedEdit;
  width: number;
  height: number;
  /**
   * Exact integer edit-timeline tick for capture.
   * When omitted, uses the same mid-timeline default as historical exportReel (min(45, end)).
   */
  tick?: number;
  scoreText?: string;
  resultCardTheme?: "default" | "portfolio-alt";
  outputTickRate?: number;
}

export type CreativeArtifactExportResult =
  | {
      ok: true;
      mimeType: string;
      data: Uint8Array;
      attribution?: readonly string[];
      watermark: string;
      editTick: number;
      sourceTick: number;
      sampleFingerprint: string;
      showReconstructionLabel: boolean;
      captionText: string;
      transitionKind: string;
      transitionPhase: number;
      stickerCount: number;
    }
  | { ok: false; reason: string };

export type CreativeArtifactExportImpl = (
  request: CreativeArtifactExportRequest,
) => Promise<CreativeArtifactExportResult>;

/**
 * Factory for the hybrid renderer used by the canonical export path. Overridable
 * so headless tests can supply an adapter wired to a fake offscreen `<video>`
 * (via setVideoFactoryForTests) — production always builds a fresh adapter that
 * decodes the real muted asset.
 */
let hybridAdapterFactory: () => HybridVideoRendererAdapter = () =>
  new HybridVideoRendererAdapter();

/** Test seam: inject a preconfigured hybrid adapter; null restores production. */
export function setHybridAdapterFactoryForTests(
  factory: (() => HybridVideoRendererAdapter) | null,
): void {
  hybridAdapterFactory = factory ?? (() => new HybridVideoRendererAdapter());
}

async function defaultExportCreativeArtifact(
  request: CreativeArtifactExportRequest,
): Promise<CreativeArtifactExportResult> {
  try {
    validateEditGraphAgainstPack(request.editGraph, request.pack);
    const resolved =
      request.resolved ?? resolveEdit(request.editGraph, request.pack);
    const timeline = buildEditedTimeline(request.pack, resolved);
    const maxEdit =
      timeline.durationTicks <= 0 ? 0 : timeline.durationTicks - 1;
    const editTick =
      request.tick !== undefined
        ? requireIntegerTick(request.tick, "exportCreativeArtifact.tick", maxEdit)
        : timeline.durationTicks <= 0
          ? 0
          : Math.min(45, maxEdit);
    const sourceTick =
      timeline.durationTicks === 0 ? 0 : editTickToSourceTick(timeline, editTick);

    // Overlay/creative sampling is renderer-agnostic (no 3D recipe needed).
    const creative = sampleCreativeRenderState({
      pack: request.pack,
      resolved,
      editTick,
      sourceTick,
      seed: request.editGraph.seed,
      timeline,
    });

    // All shipped packs are hybrid-video. If a pack is not supported by the
    // hybrid renderer, fail cleanly rather than emitting a bogus card.
    const hybridAdapter = hybridAdapterFactory();
    if (!hybridAdapter.supports(request.pack)) {
      return { ok: false, reason: "unsupported-pack" };
    }

    const exportReq = {
      sourcePack: request.pack,
      editGraph: request.editGraph,
      resolved,
      width: request.width,
      height: request.height,
      outputTickRate: request.outputTickRate ?? request.pack.tickRate,
      resultCardScoreText: request.scoreText,
      resultCardTheme: request.resultCardTheme,
      tick: editTick,
    };

    // Self-contained: exportReel builds its own offscreen video from the pack.
    const result: ExportResult = await hybridAdapter.exportReel(exportReq);
    // Deterministic, renderer-agnostic fingerprint.
    const sampleFp = `hybrid:${request.pack.id}@${request.pack.version}:e${editTick}:s${sourceTick}`;

    if (!result.ok) {
      return { ok: false, reason: result.reason };
    }
    const data =
      result.data instanceof Uint8Array
        ? result.data
        : new Uint8Array(await result.data.arrayBuffer());
    return {
      ok: true,
      mimeType: result.mimeType || "image/png",
      data,
      attribution: result.attribution,
      watermark: result.watermark,
      editTick,
      sourceTick,
      sampleFingerprint: sampleFp,
      showReconstructionLabel: shouldShowReconstructionLabel(
        request.pack,
        resolved,
      ),
      captionText: creative.overlay.caption.fullText,
      transitionKind: creative.overlay.transition.kind,
      transitionPhase: creative.overlay.transition.phase,
      stickerCount: creative.overlay.stickers.length,
    };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : "export failed",
    };
  }
}

/**
 * @deprecated MF1: capture is now self-contained (HybridVideoRendererAdapter
 * builds its own offscreen video per request), so no live editor session is
 * donated to the export path. Retained as a no-op for call-site compatibility.
 */
export function registerLiveHybridAdapterForExport(
  _adapter: HybridVideoRendererAdapter | null,
): void {
  /* no-op — capture no longer depends on a live editor session */
}

let exportImpl: CreativeArtifactExportImpl = defaultExportCreativeArtifact;

/** Production entry — ArtifactPreview queue, bridge, and any pack/graph export. */
export function exportCreativeArtifact(
  request: CreativeArtifactExportRequest,
): Promise<CreativeArtifactExportResult> {
  return exportImpl(request);
}

/** Identity of the currently installed exporter (for regression spies). */
export function getExportCreativeArtifactImpl(): CreativeArtifactExportImpl {
  return exportImpl;
}

/** Test seam: replace the canonical exporter; null restores production default. */
export function setExportCreativeArtifactImplForTests(
  impl: CreativeArtifactExportImpl | null,
): void {
  exportImpl = impl ?? defaultExportCreativeArtifact;
}
